/**
 * @file Sandbox Manager — central controller.
 *
 * Wires up the UI panel, input handler, terrain regeneration, and enemy
 * lifecycle. Integrated into the game loop via the Game context.
 */

import * as THREE from 'three';
import { ENEMY_KEYS, TerrainMode, sandboxState, resetSandboxState } from './SandboxConfig.js';
import { saveDebugPrefsFrom } from '../config/DebugPrefs.js';
import { ENEMIES } from '../config/Enemies.js';
import {
  buildSandboxPanel,
  wireSandboxPanel,
  renderEnemyToggles,
  updateTerrainButtons,
  updateCounters,
  setPanelOpen,
  updateSprayButton,
  showEnemyInfo
} from './SandboxUI.js';
import { createSandboxInput } from './SandboxInput.js';
import { Health } from '../components/Health.js';
import { AIBrain } from '../components/AIBrain.js';
import { RangedAI, PlayerControlled, Weapon } from '../components/index.js';
import { createWall } from '../entities/Wall.js';
import { Events } from '../core/EventBus.js';
import { applyPlayerAmmoPolicy } from '../combat/ammoPolicy.js';

/**
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {HTMLCanvasElement} canvas Render canvas.
 * @param {HTMLElement} uiRoot HUD container.
 * @returns {{ register: () => void, update: (dt: number) => void, dispose: () => void,
 *   toggle: () => void, setEnabled: (enabled: boolean) => void }}
 */
export function createSandboxManager(ctx, canvas, uiRoot) {
  /** @type {HTMLElement | null} */
  let panel = null;
  /** @type {ReturnType<typeof createSandboxInput> | null} */
  let inputHandler = null;
  /** @type {boolean} */
  let inputRegistered = false;
  /** @type {THREE.Group | null} Flat/obstacle meshes owned by the sandbox. */
  let sandboxTerrainGroup = null;

  /**
   * Build and insert the panel into the UI root.
   * @returns {HTMLElement}
   */
  function buildPanel() {
    panel = buildSandboxPanel(uiRoot);
    uiRoot.appendChild(panel);
    renderEnemyToggles(panel.querySelector('[data-sand="enemyList"]'));
    updateTerrainButtons(panel.querySelector('[data-sand="terrainGroup"]'));
    updateSprayButton(panel);
    return panel;
  }

  /**
   * Remove flat/obstacle meshes previously added by the sandbox.
   */
  function clearSandboxTerrainMeshes() {
    if (!sandboxTerrainGroup || !ctx.scene) {
      sandboxTerrainGroup = null;
      return;
    }

    /** @type {Set<THREE.BufferGeometry>} */
    const geos = new Set();
    /** @type {Set<THREE.Material>} */
    const mats = new Set();
    sandboxTerrainGroup.traverse((obj) => {
      const mesh = /** @type {THREE.Mesh} */ (obj);
      if (mesh.geometry) geos.add(mesh.geometry);
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          for (const m of mesh.material) mats.add(m);
        } else {
          mats.add(mesh.material);
        }
      }
    });

    ctx.scene.remove(sandboxTerrainGroup);
    for (const g of geos) g.dispose();
    for (const m of mats) m.dispose();
    sandboxTerrainGroup = null;
  }

  /**
   * Tear down current arena visuals/colliders without wiping the player or
   * other live entities (unlike {@link teardownLevel}).
   */
  function disposeArenaContents() {
    clearSandboxTerrainMeshes();
    if (ctx.arena) {
      ctx.arena.dispose();
    }
  }

  /**
   * Regenerate the terrain based on current sandbox state.
   */
  function regenerateTerrain() {
    if (!ctx.arena || !ctx.scene) return;

    // Drop sandbox-spawned enemies; wave enemies stay (user can Clear All).
    inputHandler?.clearAll();

    disposeArenaContents();

    if (sandboxState.terrainMode === TerrainMode.FLAT) {
      buildFlatTerrain(ctx);
    } else if (sandboxState.terrainMode === TerrainMode.FLAT_OBSTACLES) {
      buildFlatObstaclesTerrain(ctx);
    } else {
      const ok = ctx.arena.build();
      if (!ok) {
        console.warn('[Sandbox] Full dungeon regeneration failed');
      }
    }
  }

  /**
   * Build a completely flat terrain (green plane).
   * @param {import('../core/Context.js').GameContext} context
   */
  function buildFlatTerrain(context) {
    const { scene } = context;

    const group = new THREE.Group();
    group.name = 'SandboxFlatTerrain';

    const planeGeo = new THREE.PlaneGeometry(200, 200);
    const planeMat = new THREE.MeshStandardMaterial({
      color: 0x2a4d3a,
      roughness: 0.9,
      metalness: 0.1
    });
    const plane = new THREE.Mesh(planeGeo, planeMat);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -0.01;
    plane.receiveShadow = true;
    group.add(plane);

    const gridHelper = new THREE.GridHelper(200, 100, 0x3a6d5a, 0x2a4d3a);
    gridHelper.position.y = 0.01;
    group.add(gridHelper);

    scene.add(group);
    sandboxTerrainGroup = group;

    if (context.arena) {
      context.arena.bounds = { minX: -100, maxX: 100, minZ: -100, maxZ: 100 };
      context.arena.entrance = { x: 0, z: 0 };
      context.arena.exit = { x: 40, z: 40 };
      context.arena.exitRadius = 2.5;
      context.arena._floorSpawns = [];
      context.arena.blockers = [];
      context.arena.entities = [];
      context.arena.group = group;
    }

    rebuildFlatSpawnPoints(context);
  }

  /**
   * Build a flat terrain with random obstacles (visual + spawn blockers + wall colliders).
   * @param {import('../core/Context.js').GameContext} context
   */
  function buildFlatObstaclesTerrain(context) {
    const count = sandboxState.obstacleCount || 12;

    buildFlatTerrain(context);

    if (!sandboxTerrainGroup || !context.arena) return;

    const obstacleGeo = new THREE.BoxGeometry(1, 1, 1);
    const obstacleMat = new THREE.MeshStandardMaterial({
      color: 0x6a5a4a,
      roughness: 0.85,
      metalness: 0.05
    });

    for (let i = 0; i < count; i++) {
      const w = 1 + Math.random() * 4;
      const h = 1 + Math.random() * 3;
      const d = 1 + Math.random() * 4;
      const x = (Math.random() - 0.5) * 80;
      const z = (Math.random() - 0.5) * 80;

      const obstacle = new THREE.Mesh(obstacleGeo, obstacleMat);
      obstacle.scale.set(w, h, d);
      obstacle.position.set(x, h / 2, z);
      obstacle.castShadow = true;
      obstacle.receiveShadow = true;
      sandboxTerrainGroup.add(obstacle);

      const halfX = w / 2 + 0.4;
      const halfZ = d / 2 + 0.4;
      context.arena._blockers.push({ x, z, halfX, halfZ });
      // Real collision so movement/pathing match spawn blockers.
      context.arena.entities.push(createWall(context, x, z, halfX, halfZ, 'sandbox-obstacle'));
    }

    rebuildFlatSpawnPoints(context);
  }

  /**
   * Rebuild spawn points for flat terrains.
   * @param {import('../core/Context.js').GameContext} context
   */
  function rebuildFlatSpawnPoints(context) {
    if (!context.arena) return;
    context.arena._floorSpawns = [];

    const spacing = 4;
    const range = 80;
    for (let x = -range; x <= range; x += spacing) {
      for (let z = -range; z <= range; z += spacing) {
        const blocked = context.arena.blockers.some((b) => {
          return Math.abs(b.x - x) < b.halfX && Math.abs(b.z - z) < b.halfZ;
        });
        if (!blocked) {
          context.arena._floorSpawns.push({ x, z, roomId: 0 });
        }
      }
    }
  }

  /**
   * Enable or disable sandbox mode (panel + input).
   * @param {boolean} enabled
   */
  function setEnabled(enabled) {
    sandboxState.enabled = enabled;

    if (panel) {
      setPanelOpen(panel, enabled);
      const toggleEl = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-sand="toggle"]'));
      if (toggleEl) toggleEl.checked = enabled;
    }

    if (enabled) {
      if (!inputHandler) inputHandler = createSandboxInput(ctx, canvas);
      if (!inputRegistered) {
        inputHandler.register();
        inputRegistered = true;
      }
    } else if (inputRegistered && inputHandler) {
      inputHandler.unregister();
      inputRegistered = false;
      sandboxState.sprayMode = false;
      if (panel) updateSprayButton(panel);
    }
  }

  /**
   * Wire up panel events.
   */
  /** Persists the sandbox knobs so a reload comes back exactly as left. */
  function savePrefs() {
    saveDebugPrefsFrom({ sandbox: sandboxState });
  }

  /** Syncs the static inputs (master toggle, cheats) with the live state. */
  function syncPanelFromState() {
    const toggleEl = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-sand="toggle"]'));
    if (toggleEl) toggleEl.checked = sandboxState.enabled;
    for (const input of panel.querySelectorAll('[data-cheat]')) {
      const key = input.getAttribute('data-cheat');
      /** @type {HTMLInputElement} */ (input).checked = Boolean(sandboxState.cheats[key]);
    }
    if (sandboxState.enabled) setEnabled(true);
  }

  function wirePanel() {
    if (!panel) buildPanel();
    syncPanelFromState();

    wireSandboxPanel(panel, {
      onToggle: (enabled) => {
        setEnabled(enabled);
        savePrefs();
      },

      onSpray: (spray) => {
        sandboxState.sprayMode = spray;
        updateSprayButton(panel);
      },

      onSprayType: (type) => {
        sandboxState.sprayType = type;
        savePrefs();
        renderEnemyToggles(panel.querySelector('[data-sand="enemyList"]'));
      },

      onTerrain: (terrain) => {
        sandboxState.terrainMode = terrain;
        savePrefs();
        updateTerrainButtons(panel.querySelector('[data-sand="terrainGroup"]'));
        regenerateTerrain();
      },

      onReset: () => {
        resetSandboxState();
        savePrefs();
        for (const input of panel.querySelectorAll('[data-cheat]')) {
          /** @type {HTMLInputElement} */ (input).checked = false;
        }
        renderEnemyToggles(panel.querySelector('[data-sand="enemyList"]'));
        updateTerrainButtons(panel.querySelector('[data-sand="terrainGroup"]'));
        updateSprayButton(panel);
        regenerateTerrain();
        inputHandler?.clearAll();
      },

      onClearAll: () => {
        inputHandler?.clearAll();
      },

      onCheatToggle: (key, enabled) => {
        if (key in sandboxState.cheats) sandboxState.cheats[key] = enabled;
        savePrefs();
      },

      // Destroys every enemy on the map — wave spawns, pre-placed and
      // sandbox-sprayed alike. Silent (no DIED event): a debug wipe must not
      // shower the floor with drops or bump the score.
      onKillEnemies: () => {
        for (const entity of [...ctx.world.query(AIBrain)]) ctx.world.destroyEntity(entity);
        for (const entity of [...ctx.world.query(RangedAI)]) ctx.world.destroyEntity(entity);
        inputHandler?.clearAll();
      },

      // Hands the player the chosen weapon, replacing whatever it carries.
      // The old gun is unequipped (cancels reloads) and simply discarded — a
      // switcher is for testing loadouts, not for littering the floor.
      onGiveWeapon: (weaponKey) => {
        const player = ctx.world.query(PlayerControlled)[0];
        if (!player) return;
        const carried = player.get(Weapon);
        if (carried) {
          carried.unequip();
          player.remove(Weapon);
        }
        const weapon = applyPlayerAmmoPolicy(
          Weapon.fromArchetype(weaponKey),
          ctx.config?.ammo?.emptyMagazinePolicy
        );
        player.add(weapon.equip(player));
        ctx.playerWeaponKey = weapon.key;
        ctx.events.emit(Events.WEAPON_CHANGED, { weapon });
      },

      onRefillTimer: () => {
        const player = ctx.world.query(PlayerControlled, Health)[0];
        const health = player?.get(Health);
        if (health) health.killTimer = health.killTimerMax;
      },

      onClose: () => {
        setEnabled(false);
      },

      onEnemyToggle: (key) => {
        sandboxState.enemyToggles[key] = !sandboxState.enemyToggles[key];
        savePrefs();
        // Prefer an enabled type for spray when the current one is disabled.
        if (!sandboxState.enemyToggles[sandboxState.sprayType]) {
          const next = ENEMY_KEYS.find((k) => sandboxState.enemyToggles[k]);
          if (next) sandboxState.sprayType = next;
        }
        renderEnemyToggles(panel.querySelector('[data-sand="enemyList"]'));
      },

      onEntityClick: (entityId) => {
        const entity = ctx.world.getEntity(entityId);
        if (entity && entity.has(AIBrain)) {
          const arch = ENEMIES[entity.archetypeKey] || ENEMIES.grunt;
          const healthComp = entity.get(Health);
          const health = healthComp ? healthComp.current : arch.maxHealth;
          showEnemyInfo(panel, arch, health);
        }
      }
    });
  }

  /**
   * Register the sandbox with the game context.
   */
  function register() {
    wirePanel();
  }

  /**
   * Update the panel counters each frame.
   * @param {number} _dt
   */
  function update(_dt) {
    // Topping the magazine up every frame means it never reaches empty, so
    // reloads never trigger and the weapon component stays untouched.
    if (sandboxState.cheats.infiniteAmmo) {
      for (const entity of ctx.world.query(PlayerControlled, Weapon)) {
        const weapon = entity.get(Weapon);
        weapon.currentAmmo = weapon.magazineCapacity;
      }
    }

    // Drop orphan flat/obstacle meshes if the level was torn down or rebuilt
    // outside the sandbox (e.g. death → Loading → new dungeon).
    if (sandboxTerrainGroup && (!ctx.arena || ctx.arena.group !== sandboxTerrainGroup)) {
      clearSandboxTerrainMeshes();
    }

    if (!panel || !sandboxState.enabled) return;

    const aiEntities = ctx.world.query(AIBrain);
    let totalAlive = 0;
    /** @type {Record<string, number>} */
    const counts = {};

    for (const key of ENEMY_KEYS) {
      counts[key] = 0;
    }

    for (const entity of aiEntities) {
      if (!entity.alive) continue;
      const key = entity.archetypeKey;
      if (key && counts[key] !== undefined) {
        counts[key]++;
        totalAlive++;
      }
    }

    updateCounters(panel, totalAlive, counts);
  }

  /**
   * Dispose all sandbox resources.
   */
  function dispose() {
    if (inputHandler) {
      inputHandler.unregister();
      inputHandler.clearAll();
      inputHandler = null;
    }
    inputRegistered = false;

    clearSandboxTerrainMeshes();

    if (panel && panel.parentNode) {
      panel.parentNode.removeChild(panel);
    }
    panel = null;
    sandboxState.enabled = false;
    sandboxState.sprayMode = false;
  }

  /**
   * Toggle the sandbox on/off.
   */
  function toggle() {
    setEnabled(!sandboxState.enabled);
  }

  return {
    register,
    update,
    dispose,
    toggle,
    setEnabled,
    regenerateTerrain,
    get panel() {
      return panel;
    },
    get state() {
      return sandboxState;
    }
  };
}
